import { create } from 'zustand'

import { useSessionStore } from '@/lib/auth/session-store'

import { profileSocialApi } from '../../features/profile-social/api'
import {
  buildFriendIndex,
  type FriendIdIndex,
} from './person-name'

/**
 * Friend ids backing the privacy-aware person names (D-101). Cached once per
 * signed-in identity; every unknown / failed state collapses to the
 * abbreviated form because "unknown" must never leak a surname.
 */
interface FriendIndexState {
  ids: FriendIdIndex | null
  pending: Promise<FriendIdIndex> | null
  load: () => Promise<void>
  /** Refetch after a friendship mutation (accept/remove). */
  refresh: () => void
  /** Adopt a trusted friends payload fetched by a friendship-gated surface. */
  seed: (ids: FriendIdIndex) => void
  reset: () => void
}

export const useFriendIndexStore = create<FriendIndexState>()((set, get) => ({
  ids: null,
  pending: null,
  load: async () => {
    if (get().pending || get().ids) return
    const pending = profileSocialApi
      .listFriends()
      .then((response) => {
        const ids = buildFriendIndex(response)
        set({ ids, pending: null })
        return ids
      })
      .catch(() => {
        const empty: FriendIdIndex = new Set()
        set({ ids: empty, pending: null })
        return empty
      })
    set({ pending })
    await pending
  },
  reset: () => set({ ids: null, pending: null }),
  refresh: () => {
    get().reset()
    void get().load()
  },
  seed: (ids) => set({ ids, pending: null }),
}))

/** Hooks the store refresh into the signed-in identity like chat drafts. */
let lastUserId = useSessionStore.getState().user?.id ?? null
useSessionStore.subscribe((state) => {
  const userId = state.user?.id ?? null
  if (userId === lastUserId) return
  lastUserId = userId
  useFriendIndexStore.getState().reset()
})

export function useFriendIndex(): FriendIdIndex | null {
  return useFriendIndexStore((state) => state.ids)
}
